/* cf-perfmesh-panel-truth.js — Click Fencing
 *
 * Defect (measured on live, 2026-08-25):
 *   When the PerfMesh solver falls into its mixed-sheet branch, the last drawn
 *   panel and one post are drawn but never billed.
 *     9250mm  / 3000 sheets -> drew 5 posts + 3000+3000+2000+1000, billed 4 posts + 2x3m + 1x2m
 *     12300mm / 3000 sheets -> drew 6 posts + 3000+3000+3000+2000+1000, billed 5 posts + 3x3m + 1x2m
 *   Each short by one 2m kit ($467.56) and one post ($50.16) = $517.72, and the
 *   shortfall carried into the saved quote and Continue to Payment.
 *
 *   Cause: the quote is built from calculation.panelWidths (the pre-mixed plan)
 *   while the preview is drawn from the mixed plan, and postCount is derived from
 *   the same short list (panelWidths.length + 1).
 *
 * Pete's rule (2026-08-25):
 *   A mesh FENCE PANEL is a 2000 or 3000 kit only. 800 and 970 are gates, welded,
 *   and cannot be used as fence panels. A short panel is cut from the smallest kit
 *   that covers it:  <= 2000mm -> 2m kit, otherwise -> 3m kit.
 *
 * What this does:
 *   Reconciles the bill against the panels actually drawn, in the SAVED QUOTE in
 *   localStorage (which feeds /pages/quote-review, the cart and checkout) and in the
 *   cartItems on that quote.  That object is written by the calculator in one piece,
 *   so its drawing and its breakdown always describe the same section.
 *
 *   It deliberately does NOT rewrite the on-screen Review & Checkout breakdown.
 *   An earlier version did, reading the rendered drawing and topping the breakdown up
 *   to match.  Break-testing on 2026-08-25 showed that over-charges: go to Review,
 *   press Back, and shorten the run, and for a moment the OLD drawing sits beside the
 *   NEW breakdown.  The correction then wrote the old, larger quantities onto the new
 *   quote and they stuck.  Measured on the preview theme: 12300 -> 7300 quoted
 *   $3,252.50 instead of $2,012.48; 7300 -> 5000 quoted $2,012.48 instead of
 *   $1,290.18; 12300 -> 9250 quoted $3,252.50 instead of $2,530.20.  The live theme
 *   was correct in all three, so this was self-inflicted.  Under-stating a price on
 *   one screen is recoverable; over-charging a customer is not, so the screen
 *   correction is gone and the money is corrected where it is actually taken.
 *   It also repairs the collapsed legacy preview that the same mixed-sheet plans
 *   trigger (posts rendered at width:0 and panels at margin:0 -30px).
 *
 * Safety:
 *   - Only ever ADDS missing kits/posts. Never removes a line, never reduces a
 *     quantity, never touches price per unit.
 *   - Stands down completely once the customer has adjusted their own kit
 *     (cfAdjustments present) so it can never fight a manual edit.
 *   - Every entry point is wrapped in try/catch: a failure here can only mean
 *     "no correction applied", never a broken calculator.
 *   - Idempotent: quantities are set to the required absolute value, so running
 *     twice changes nothing the second time.
 */
(function () {
  'use strict';
  if (window.__cfPerfmeshPanelTruthInstalled) return;
  window.__cfPerfmeshPanelTruthInstalled = 1;

  var LOG = '[cf-perfmesh-panel-truth v2]';
  /* '1m' must be in this list. cf-perfmesh-cut-parts.js moves any panel of 970mm
     or less off the 2 metre kit and onto the 1m PerfMesh Panel Kit. If this file
     only counted 2m and 3m kits it would see three drawn panels against two
     billed, conclude a panel had been missed, and add a 2 metre kit and a post
     back on top - measured on the preview theme at 5000mm on 2000 sheets: screen
     $1,366.76, saved quote $1,884.48, exactly one kit ($467.56) plus one post
     ($50.16) too much. The repair for an under-bill must not become an
     over-bill. */
  var KIT_RE = { '1m': /^PM-PK-1000/i, '2m': /^PERF-KIT-2M/i, '3m': /^PERF-KIT-3M/i };
  var TIERS = ['1m', '2m', '3m'];
  var SMALL_MAX = 970;                     /* the widest panel a 1m kit can make */

  function isArr(x) { return Object.prototype.toString.call(x) === '[object Array]'; }
  function num(x) { var n = parseFloat(x); return isFinite(n) ? n : 0; }
  function round2(n) { return Math.round(n * 100) / 100; }

  /* <= 970 is its own 1m kit, <= 2000 comes off a 2m kit, larger off a 3m kit. */
  function tierFor(width) {
    var w = num(width);
    if (w <= SMALL_MAX) return '1m';
    return w > 2000 ? '3m' : '2m';
  }

  function colourKey(name) {
    var s = String(name || '').toLowerCase();
    if (s.indexOf('white') >= 0) return 'white';
    if (s.indexOf('monument') >= 0) return 'monument';
    return 'black';
  }

  /* Product data comes from the shared map the four calculators read. We never
     invent a variant id — if the map is not there yet, we leave the quote alone. */
  function kitProduct(tier, colour) {
    try {
      var gc = window.glassCalculator || window.glassFencingCalculator;
      var kits = gc && gc.shopifyData && gc.shopifyData.products &&
                 gc.shopifyData.products.perfmeshKits;
      var k = kits && kits[tier] && kits[tier][colour];
      if (!k || !k.id) return null;
      return { variantId: k.id, sku: k.sku, price: num(k.price) / 100 };
    } catch (e) { return null; }
  }

  function isPanel(p) {
    if (!p) return false;
    if (p.isGate || p.isRaked) return false;
    var t = String(p.type || '').toLowerCase();
    /* 2026-08-27: a gated section carries a phantom { type: 'hinge' } entry in
       renderedPanels that is never drawn and never billed. Counting it as a mesh
       panel made this patch add a whole extra kit + post at quote-review, so the
       cart total jumped above the calculator's instant quote (6000mm + gate:
       $2,032.05 on screen, $2,754.35 in the cart). Only real mesh panels count. */
    if (t.indexOf('gate') >= 0 || t.indexOf('rake') >= 0 ||
        t.indexOf('hinge') >= 0 || t.indexOf('post') >= 0 ||
        t.indexOf('gap') >= 0) return false;
    return num(p.width) > 0;
  }

  /* ------------------------------------------------------------------ *
   * A. saved quote + cart items
   * ------------------------------------------------------------------ */

  function reconcileQuote(q) {
    if (!q || typeof q !== 'object') return false;
    /* the saved quote spells it "PerfMesh"; the localStorage keys spell it
       "perfmesh". Compare case-insensitively or this never fires. */
    if (String(q.calcType || '').toLowerCase() !== 'perfmesh') return false;
    if (!isArr(q.breakdown) || !q.breakdown.length) return false;
    if (!q.sections || typeof q.sections !== 'object') return false;

    /* the customer has hand-adjusted this kit — never override that */
    if (q.cfAdjustments && Object.keys(q.cfAdjustments).length) return false;

    var need = { '1m': 0, '2m': 0, '3m': 0 }, drawn = 0, colour = null, k, i;

    for (k in q.sections) {
      if (!Object.prototype.hasOwnProperty.call(q.sections, k)) continue;
      var sec = q.sections[k];
      if (!sec) continue;
      var panels = isArr(sec.renderedPanels) ? sec.renderedPanels
                 : isArr(sec.panels) ? sec.panels : null;
      if (!panels) return false;                       /* no drawing to trust */
      var c = colourKey(sec.colour || sec.color);
      /* a mixed-colour job would need per-colour kit lines; leave it alone
         rather than bill the wrong finish */
      if (colour && c !== colour) return false;
      colour = c;
      for (i = 0; i < panels.length; i++) {
        if (!isPanel(panels[i])) continue;
        need[tierFor(panels[i].width)]++;
        drawn++;
      }
    }
    if (!drawn) return false;
    if (!colour) colour = 'black';

    /* what the bill currently carries */
    var have = { '1m': 0, '2m': 0, '3m': 0 }, line = { '1m': null, '2m': null, '3m': null };
    var postLine = null, billed = 0;
    for (i = 0; i < q.breakdown.length; i++) {
      var l = q.breakdown[i];
      if (!l) continue;
      var sku = String(l.sku || '');
      for (var t = 0; t < TIERS.length; t++) {
        if (KIT_RE[TIERS[t]].test(sku)) {
          have[TIERS[t]] += num(l.quantity);
          if (!line[TIERS[t]]) line[TIERS[t]] = l;
        }
      }
      if (!postLine && /post/i.test(String(l.item || '')) && !/bracket|cap|kit/i.test(String(l.item || ''))) {
        postLine = l;
      }
    }
    /* every tier, including 1m. Leaving 1m out of this sum was the bug that let a
       correctly-billed quote look under-billed by one panel. */
    for (var tb = 0; tb < TIERS.length; tb++) billed += have[TIERS[tb]];
    if (!billed) return false;               /* nothing to reconcile against */
    if (drawn <= billed) return false;       /* already right, or already fixed */

    /* the SKUs already on the quote are the most reliable statement of finish */
    var anyKit = line['2m'] || line['3m'] || line['1m'];
    if (anyKit && anyKit.sku) {
      if (/-WHT$/i.test(anyKit.sku)) colour = 'white';
      else if (/-MON$/i.test(anyKit.sku)) colour = 'monument';
      else if (/-BLK$/i.test(anyKit.sku)) colour = 'black';
    }

    var changed = false;

    for (var ti = 0; ti < TIERS.length; ti++) {
      var tier = TIERS[ti];
      if (need[tier] <= have[tier]) continue;
      var target = need[tier];
      if (line[tier]) {
        line[tier].quantity = target;
        line[tier].totalPrice = round2(num(line[tier].unitPrice) * target);
        changed = true;
      } else {
        var prod = kitProduct(tier, colour);
        if (!prod) continue;                 /* no product data — do not guess */
        var template = line['2m'] || line['3m'] || line['1m'] || q.breakdown[0];
        q.breakdown.push({
          category: (template && template.category) || 'Panel Kits',
          item: 'PerfMesh Pool Fencing Panel Kit — ' + tier.charAt(0) +
                ' Metre, ' + (colour === 'white' ? 'Satin White' : colour === 'monument' ? 'Monument' : 'Satin Black') +
                ' (DIY Flat Pack)',
          sku: prod.sku,
          quantity: target,
          unitPrice: prod.price,
          totalPrice: round2(prod.price * target),
          variantId: prod.variantId,
          section: (template && template.section) || 1,
          colour: (template && template.colour) || null
        });
        line[tier] = q.breakdown[q.breakdown.length - 1];
        changed = true;
      }
    }

    if (!changed) return false;

    /* one post per panel joint plus the two ends: the calculator's own formula,
       fed the panel count it should have used. Delta is zero on a second run. */
    if (postLine) {
      var extra = drawn - billed;
      postLine.quantity = num(postLine.quantity) + extra;
      postLine.totalPrice = round2(num(postLine.unitPrice) * postLine.quantity);
    }

    /* totals */
    var sum = 0;
    for (i = 0; i < q.breakdown.length; i++) sum += num(q.breakdown[i] && q.breakdown[i].totalPrice);
    q.grandTotal = round2(sum);

    /* Re-baseline the quote editor. cfBaseline is what the customer's "Adjust
       your kit" screen diffs against; leaving it at the old quantities would
       show our correction as if the customer had added the panel themselves. */
    if (q.cfBaseline && typeof q.cfBaseline === 'object' && !isArr(q.cfBaseline)) {
      var base = {};
      for (i = 0; i < q.breakdown.length; i++) {
        var bl = q.breakdown[i];
        if (!bl) continue;
        base[(bl.section == null ? '' : bl.section) + '|' + (bl.category || '') + '|' +
             (bl.item || '') + '|' + (bl.sku == null ? '' : bl.sku) + '|' + i] = num(bl.quantity);
      }
      q.cfBaseline = base;
    }

    /* mirror into the cart */
    if (isArr(q.cartItems)) {
      for (ti = 0; ti < TIERS.length; ti++) {
        var tr = TIERS[ti];
        if (!line[tr]) continue;
        var vid = line[tr].variantId, found = null;
        for (i = 0; i < q.cartItems.length; i++) {
          var ci = q.cartItems[i];
          if (ci && (ci.variantId === vid || ci.id === vid)) { found = ci; break; }
        }
        if (found) found.quantity = line[tr].quantity;
        else if (vid) q.cartItems.push({
          title: line[tr].item, variantId: vid, quantity: line[tr].quantity,
          price: line[tr].unitPrice, sku: line[tr].sku
        });
      }
      if (postLine) {
        for (i = 0; i < q.cartItems.length; i++) {
          var pc = q.cartItems[i];
          if (pc && /post/i.test(String(pc.title || '')) && !/bracket|cap|kit/i.test(String(pc.title || ''))) {
            pc.quantity = postLine.quantity;
            break;
          }
        }
      }
    }

    try {
      console.log(LOG, 'corrected quote', q.quoteName || q.quoteNum,
        'panels drawn', drawn, 'kits billed was', billed, 'now', need['1m'] + need['2m'] + need['3m'],
        'total now', q.grandTotal);
    } catch (e) {}
    return true;
  }

  function reconcilePayload(key, value) {
    var o = JSON.parse(value);
    var touched = false, i;
    if (key === 'perfmesh_quotes' && isArr(o)) {
      for (i = 0; i < o.length; i++) if (reconcileQuote(o[i])) touched = true;
    } else if (o && typeof o === 'object') {
      touched = reconcileQuote(o);
    }
    return touched ? JSON.stringify(o) : null;
  }

  var WATCHED = { 'perfmesh_active_quote': 1, 'perfmesh_quotes': 1 };
  var nativeSet = window.Storage && window.Storage.prototype.setItem;
  if (nativeSet && !window.__cfPanelTruthStorageWrapped) {
    window.__cfPanelTruthStorageWrapped = 1;
    window.Storage.prototype.setItem = function (key, value) {
      try {
        if (WATCHED[key] && typeof value === 'string') {
          var fixed = reconcilePayload(key, value);
          if (fixed) value = fixed;
        }
      } catch (e) { /* fall through and store the original */ }
      return nativeSet.call(this, key, value);
    };
  }

  /* anything already saved before this patch shipped */
  function sweepStored() {
    try {
      for (var key in WATCHED) {
        var raw = localStorage.getItem(key);
        if (!raw) continue;
        var fixed = reconcilePayload(key, raw);
        if (fixed) nativeSet.call(localStorage, key, fixed);
      }
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ *
   * B. the on-screen Review & Checkout breakdown
   * ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------ *
   * C. the collapsed legacy preview
   * ------------------------------------------------------------------ */

  function repairLegacyPreviews() {
    var containers = document.querySelectorAll('.section-panels-container');
    for (var c = 0; c < containers.length; c++) {
      var el = containers[c];
      if (el.querySelector('svg')) continue;
      if (el.getAttribute('data-cf-preview-rebuilt') === '1') continue;
      if (!el.querySelector('.gap-preview')) continue;

      var parts = [], kids = el.children, i;
      for (i = 0; i < kids.length; i++) {
        var txt = String(kids[i].textContent || '').replace(/\s+/g, ' ').trim();
        var pm = /^(\d+)mm\s*Post$/i.exec(txt);
        var sm = /^(\d+)mm\s*Sheet$/i.exec(txt) || /^(\d+)mm\s*Panel$/i.exec(txt);
        if (pm) parts.push({ kind: 'post', mm: parseInt(pm[1], 10) });
        else if (sm) parts.push({ kind: 'panel', mm: parseInt(sm[1], 10) });
      }
      if (parts.length < 3) continue;

      var total = 0;
      for (i = 0; i < parts.length; i++) total += parts[i].mm;
      if (!total) continue;

      var W = Math.max(320, Math.round(el.clientWidth || 640) - 16);
      var POST_PX = 8, MIN_PANEL_PX = 26;
      var postCount = 0;
      for (i = 0; i < parts.length; i++) if (parts[i].kind === 'post') postCount++;
      var panelSpace = W - postCount * POST_PX;
      var panelMm = total - postCount * 50;
      if (panelSpace <= 0 || panelMm <= 0) continue;

      var svgNs = 'http://www.w3.org/2000/svg';
      var svg = document.createElementNS(svgNs, 'svg');
      svg.setAttribute('viewBox', '0 0 ' + W + ' 206');
      svg.setAttribute('width', String(W));
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

      function rect(x, y, w, h, fill, stroke) {
        var r = document.createElementNS(svgNs, 'rect');
        r.setAttribute('x', x); r.setAttribute('y', y);
        r.setAttribute('width', w); r.setAttribute('height', h);
        r.setAttribute('fill', fill);
        if (stroke) { r.setAttribute('stroke', '#1a1a1a'); r.setAttribute('stroke-width', '2'); }
        svg.appendChild(r);
      }
      function label(x, y, s) {
        var t = document.createElementNS(svgNs, 'text');
        t.setAttribute('x', x); t.setAttribute('y', y);
        t.setAttribute('text-anchor', 'middle');
        t.setAttribute('font-size', '9');
        t.setAttribute('font-family', 'sans-serif');
        t.setAttribute('fill', '#475569');
        t.setAttribute('dominant-baseline', 'hanging');
        t.textContent = s;
        svg.appendChild(t);
      }

      var x = 0;
      for (i = 0; i < parts.length; i++) {
        var part = parts[i];
        if (part.kind === 'post') {
          rect(x, 20, POST_PX, 150, '#1a1a1a', false);
          rect(x - 5, 164, 18, 6, '#1a1a1a', false);
          label(x + POST_PX / 2, 174, part.mm + 'mm Post');
          x += POST_PX;
        } else {
          var w = Math.max(MIN_PANEL_PX, Math.round(panelSpace * (part.mm / panelMm)));
          rect(x, 20, w, 122, '#c8c8c8', true);
          label(x + w / 2, 174, part.mm + 'mm');
          label(x + w / 2, 186, 'Panel');
          x += w;
        }
      }

      el.setAttribute('data-cf-preview-rebuilt', '1');
      el.style.overflowX = 'auto';
      el.innerHTML = '';
      el.appendChild(svg);
      try { console.log(LOG, 'rebuilt collapsed preview,', parts.length, 'components'); } catch (e) {}
    }
  }

  /* ------------------------------------------------------------------ *
   * wiring
   * ------------------------------------------------------------------ */

  var pending = null;
  function run() {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      try { repairLegacyPreviews(); } catch (e) { try { console.warn(LOG, 'preview', e); } catch (e2) {} }
    }, 350);
  }

  function start() {
    sweepStored();
    run();
    try {
      new MutationObserver(run).observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
    try { console.log(LOG, 'installed'); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
